import React from 'react';
import { Box, Text } from '@mantine/core';
import type { FormAttribute } from './EntityUpsertForm.types';
import { getEntityValueFromARef, normalizeKey, type EntityRef } from '../../types';
import { NON_INDEXED_PROPERTY_STUB } from '../../utils/entityPropertyUtils';
import { detectFieldType } from '../../utils/fieldTypeDetection';
import {
    DeferredLoadPlaceholder,
    FieldLabelWithRequired,
    getDynamicFieldRendererDescriptor,
    renderDynamicField,
    type DynamicFieldAttribute,
    type DynamicFieldRuntime,
    type DynamicFieldSupplies,
    type GetDynamicFieldRendererDescriptorInput,
} from './renderDynamicField';
import { DeferredArrayRefStubField } from './DeferredArrayRefStubField';
import { ScimAceEditor } from '../common/ScimAceEditor';
import { SqlEditorWithFullscreen } from '../common/SqlEditorWithFullscreen';
import { getEntityUpsertSession } from './entityUpsertRenderField/EntityUpsertSessionContext';
import { formatTier3AttributeSizeEstimate } from '../../types/EntityEntitlements';

export interface RenderEntityUpsertFormFieldParams {
    attr: FormAttribute;
    value: unknown;
    onChange: (value: unknown) => void;
    renderField: (attr: FormAttribute) => React.ReactNode;
}

const POSSESSIVE_NAMES = [
    'possessivescope',
    'possessivetargetattribute',
    'possessiveactorsattribute',
    'possessiveactorsattributes',
];

/**
 * Single field renderer for EntityUpsertForm: dynamic resolver + delegate to full renderField for defaults/possessive.
 */
export function renderEntityUpsertFormField(params: RenderEntityUpsertFormFieldParams): React.ReactNode {
    const { attr, value, onChange, renderField } = params;
    const {
        wrapWithFieldDebug,
        isRequestApprovalType,
        entityType,
        formData,
        existingEntity,
        isEditMode,
        entityId,
        isViewSurface,
        loading,
        formKeyFromAttrName,
        nonIndexedHasLengthCountNamesSet,
        deferredArrayRefNamesSet,
        isEmptyForDeferLoad,
        deferredArrayPendingAdds,
        deferredArrayPendingRemoves,
        onRequestAttributeValue,
        loadingAttribute,
        setPaginatedArrayRefViewer,
        setDeferredArrayAddField,
        setMultiRefBrowserField,
        getReferenceDisplayName,
        updateReferenceEntity,
        clearReferenceEntity,
        entityService,
        handleFieldChange,
        multiRefBrowserField,
        targetEntitlement,
    } = getEntityUpsertSession();

    const name = attr.name || attr.id || attr.value;
    const label = attr.label || name;
    const tier3SizeEstimate = name ? formatTier3AttributeSizeEstimate(String(name), targetEntitlement) : null;
    const keysToCheck = [attr?.name, attr?.id].filter(Boolean).map((k) => normalizeKey(String(k)));
    const isPossessiveAttr = keysToCheck.some((n) => POSSESSIVE_NAMES.includes(n));
    if (isRequestApprovalType && isPossessiveAttr) {
        return renderField(attr);
    }

    const isExternalManagedForm =
        entityType?.toLowerCase() === 'group' &&
        String(getEntityValueFromARef('ManagedType', formData) ?? (existingEntity as any)?.ManagedType ?? '').toLowerCase() ===
            'external';
    const isJsonType =
        (attr.type ?? '').toString().toLowerCase() === 'json' ||
        (attr.type ?? '').toString().toLowerCase() === 'object' ||
        (attr.type ?? '').toString().toLowerCase() === 'complex';
    const formValue = name ? (getEntityValueFromARef(name, formData) ?? '') : value;
    const effectiveValue = formValue ?? value;
    const isStubValue =
        effectiveValue === NON_INDEXED_PROPERTY_STUB ||
        (!!name && nonIndexedHasLengthCountNamesSet.has(normalizeKey(name)) && isEmptyForDeferLoad(effectiveValue));

    const renderDeferredStub = (readOnlyField: boolean) => {
        if (!name) return null;
        const fieldName = name;
        const pendingAdds = deferredArrayPendingAdds[fieldName] ?? [];
        const pendingRemoves = deferredArrayPendingRemoves[fieldName] ?? [];
        return wrapWithFieldDebug(
            fieldName,
            <DeferredArrayRefStubField
                fieldName={fieldName}
                fieldLabel={String(label ?? fieldName)}
                readOnly={readOnlyField}
                pendingAddsCount={pendingAdds.length}
                pendingRemovesCount={pendingRemoves.length}
                onView={() => setPaginatedArrayRefViewer({ field: fieldName, mode: 'view' })}
                onAddItems={readOnlyField ? undefined : () => setDeferredArrayAddField(fieldName)}
                onRemoveItems={readOnlyField ? undefined : () => setPaginatedArrayRefViewer({ field: fieldName, mode: 'remove' })}
            />,
            'DeferredLoadPlaceholder',
            isStubValue
        );
    };

    if (isStubValue && isEditMode && entityId && name) {
        if (deferredArrayRefNamesSet.has(normalizeKey(name))) {
            return renderDeferredStub(attr.readOnly === true || isExternalManagedForm || isViewSurface);
        }
        if (onRequestAttributeValue) {
            return wrapWithFieldDebug(
                name,
                <DeferredLoadPlaceholder
                    fieldName={name}
                    fieldLabel={label}
                    readOnly={attr.readOnly === true || isExternalManagedForm}
                    loading={loadingAttribute === name}
                    onLoad={async () => {
                        const v = await onRequestAttributeValue(name);
                        onChange(v ?? '');
                    }}
                    tooltipLabel="Sensitive, Large, or File value"
                    sizeEstimate={tier3SizeEstimate}
                />,
                'DeferredLoadPlaceholder',
                isStubValue
            );
        }
    }

    if (isJsonType && !isStubValue && formValue !== undefined && formValue !== null) {
        const editorValue =
            typeof formValue === 'string'
                ? formValue
                : typeof formValue === 'object'
                  ? JSON.stringify(formValue, null, 2)
                  : String(formValue);
        const aceReadOnly = loading || attr.readOnly === true || isExternalManagedForm || isViewSurface;
        const jsonEmpty = editorValue === '' || editorValue === '{}' || editorValue === '[]';
        if (aceReadOnly && jsonEmpty) {
            return wrapWithFieldDebug(
                name ?? '',
                <Box key={name} mb="md">
                    <Text size="sm" fw={500} mb={4}>
                        <FieldLabelWithRequired label={label ?? ''} required={attr.required === true} readOnly />
                    </Text>
                    <Text size="sm" c="dimmed">
                        No value
                    </Text>
                </Box>,
                'AceEditor-json-placeholder',
                isStubValue
            );
        }
        return wrapWithFieldDebug(
            name ?? '',
            <Box key={name} mb="md">
                <Text size="sm" fw={500} mb={4}>
                    <FieldLabelWithRequired label={label ?? ''} required={attr.required === true} readOnly={aceReadOnly} />
                </Text>
                <ScimAceEditor
                    mode="json"
                    value={editorValue}
                    onChange={(str) => {
                        try {
                            onChange(JSON.parse(str));
                        } catch {
                            onChange(str);
                        }
                    }}
                    readOnly={aceReadOnly}
                    height="200px"
                />
            </Box>,
            'AceEditor-json',
            isStubValue
        );
    }

    if (!isStubValue && name && entityType) {
        const fieldType = detectFieldType(name, effectiveValue, entityType, formData);
        if (fieldType.isSqlField) {
            const sqlValue = typeof formValue === 'string' ? formValue : String(formValue ?? '');
            const aceReadOnlySql = loading || attr.readOnly === true || isExternalManagedForm || isViewSurface;
            if (aceReadOnlySql && sqlValue === '') {
                return wrapWithFieldDebug(
                    name,
                    <Box key={name} mb="md">
                        <Text size="sm" fw={500} mb={4}>
                            <FieldLabelWithRequired label={label ?? ''} required={attr.required === true} readOnly />
                        </Text>
                        <Text size="sm" c="dimmed">
                            No value
                        </Text>
                    </Box>,
                    'AceEditor-sql-placeholder',
                    isStubValue
                );
            }
            return wrapWithFieldDebug(
                name,
                <Box key={name} mb="md">
                    <SqlEditorWithFullscreen
                        labelRow={
                            <Text size="sm" fw={500} component="span">
                                <FieldLabelWithRequired label={label ?? ''} required={attr.required === true} readOnly={aceReadOnlySql} />
                            </Text>
                        }
                        value={sqlValue}
                        onChange={(str) => onChange(str)}
                        readOnly={aceReadOnlySql}
                        fieldName={String(name ?? 'sql')}
                        modalTitle={typeof label === 'string' ? label : 'SQL'}
                    />
                </Box>,
                'AceEditor-sql',
                isStubValue
            );
        }
    }

    const effectiveReadOnly = attr.readOnly === true || isExternalManagedForm || isViewSurface;
    const resolverInput: GetDynamicFieldRendererDescriptorInput = {
        attr: attr as DynamicFieldAttribute,
        entityType: entityType ?? null,
        formKeyFromAttrName,
        readOnly: effectiveReadOnly,
        loading,
    };
    const descriptor = getDynamicFieldRendererDescriptor(attr as DynamicFieldAttribute, resolverInput);

    if (descriptor.renderer === 'DefaultText' && isStubValue && isEditMode && entityId && name) {
        if (deferredArrayRefNamesSet.has(normalizeKey(name))) {
            return renderDeferredStub(attr.readOnly === true || isViewSurface);
        }
        if (onRequestAttributeValue) {
            return wrapWithFieldDebug(
                name ?? '',
                <DeferredLoadPlaceholder
                    fieldName={name}
                    fieldLabel={label}
                    readOnly={attr.readOnly === true || isExternalManagedForm}
                    loading={loadingAttribute === name}
                    onLoad={async () => {
                        const v = await onRequestAttributeValue(name);
                        onChange(v ?? '');
                    }}
                    tooltipLabel="Sensitive, Large, or File value"
                    sizeEstimate={tier3SizeEstimate}
                />,
                'DeferredLoadPlaceholder',
                isStubValue
            );
        }
    }

    if (descriptor.renderer !== 'DefaultText') {
        const supplies: DynamicFieldSupplies = {
            getReferenceDisplayName,
            onReferenceEntityUpdate: updateReferenceEntity,
            onReferenceEntityClear: clearReferenceEntity,
            refMultiOpenField: multiRefBrowserField,
            setRefMultiOpenField: setMultiRefBrowserField,
            openRefMultiViewer: (fieldName: string) => {
                if (!entityId) return;
                setPaginatedArrayRefViewer({ field: fieldName, mode: 'view' });
            },
            entityService,
        };
        const runtime: DynamicFieldRuntime = {
            formData,
            onFieldChange: handleFieldChange,
            formKeyFromAttrName,
            entityType,
            readOnly: effectiveReadOnly,
            loading,
            supplies,
        };
        return wrapWithFieldDebug(
            name ?? '',
            renderDynamicField(attr as DynamicFieldAttribute, runtime),
            descriptor.renderer,
            isStubValue
        );
    }

    return renderField(attr);
}
